import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, Video, X, Check, RefreshCw, Phone, Mail, Plus, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { engineers } from '../../mock';

const API = '/api';

const PAYMENT_METHODS = [
  { id: 'cashapp', label: 'Cash App', color: 'bg-green-600' },
  { id: 'zelle', label: 'Zelle', color: 'bg-purple-600' },
  { id: 'cash', label: 'Cash', color: 'bg-yellow-600' },
];

const TIME_SLOTS = [
  '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
  '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM',
  '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM'
];

const WeeklyScheduleCalendar = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day; // Start from Sunday
    return new Date(today.setDate(diff));
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState('all');
  
  // Reschedule state
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });
  const [processing, setProcessing] = useState(false);

  // Quick Book state
  const [showQuickBook, setShowQuickBook] = useState(false);
  const [quickBookDate, setQuickBookDate] = useState('');
  const [quickBook, setQuickBook] = useState({
    client_name: '',
    engineer_id: 1,
    engineer_name: 'Trap Spade',
    time: '',
    hours: 2,
    price: 100,
    payment_method: 'cashapp',
    client_email: '',
    client_phone: ''
  });
  const [submittingQuickBook, setSubmittingQuickBook] = useState(false);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [currentWeekStart]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Get dates for current week
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeekStart]);

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  // Get bookings for current week
  const weekBookings = useMemo(() => {
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    
    return bookings
      .filter(b => {
        const bookingDate = new Date(b.date + 'T00:00:00');
        const matchesWeek = bookingDate >= startDate && bookingDate <= endDate;
        const matchesEngineer = selectedEngineer === 'all' || b.engineer_id === parseInt(selectedEngineer);
        const notCancelled = b.status !== 'cancelled';
        return matchesWeek && matchesEngineer && notCancelled;
      })
      .sort((a, b) => {
        // Sort by date first, then by time
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        
        // Parse time for sorting
        const parseTime = (timeStr) => {
          if (!timeStr) return 0;
          const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!match) return 0;
          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          const period = match[3].toUpperCase();
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return hours * 60 + minutes;
        };
        
        return parseTime(a.time) - parseTime(b.time);
      });
  }, [bookings, weekDates, selectedEngineer]);

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped = {};
    weekDates.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      grouped[dateStr] = weekBookings.filter(b => b.date === dateStr);
    });
    return grouped;
  }, [weekBookings, weekDates]);

  // Get engineer color
  const getEngineerColor = (engineerId) => {
    const colors = {
      1: 'border-l-purple-500 bg-purple-500/10', // Trap Spade
      2: 'border-l-blue-500 bg-blue-500/10',     // Chapo
      3: 'border-l-green-500 bg-green-500/10',   // Dev
      4: 'border-l-orange-500 bg-orange-500/10'  // Shotz
    };
    return colors[engineerId] || 'border-l-gray-500 bg-gray-500/10';
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Handle booking click
  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
    setIsRescheduling(false);
    setRescheduleData({ date: booking.date, time: booking.time });
  };

  // Open quick book for a specific date
  const openQuickBook = (dateStr) => {
    setQuickBookDate(dateStr);
    setQuickBook({
      client_name: '',
      engineer_id: 1,
      engineer_name: 'Trap Spade',
      time: '',
      hours: 2,
      price: 100,
      payment_method: 'cashapp',
      client_email: '',
      client_phone: ''
    });
    setShowQuickBook(true);
  };

  // Submit quick book
  // Fetch client suggestions
  const fetchSuggestions = async (query) => {
    try {
      const res = await axios.get(`${API}/admin/client-suggestions`, { params: { q: query } });
      setClientSuggestions(res.data.clients || []);
      setShowSuggestions(true);
    } catch { setClientSuggestions([]); }
  };

  // Handle name input change with debounced suggestions
  const handleNameChange = (val) => {
    setQuickBook(prev => ({...prev, client_name: val}));
    if (val.length >= 2) {
      fetchSuggestions(val);
    } else if (val.length === 0) {
      fetchSuggestions('');
    } else {
      setShowSuggestions(false);
    }
  };

  // Select a suggested client
  const selectClient = (client) => {
    setQuickBook(prev => ({
      ...prev,
      client_name: client.name,
      client_email: client.email,
      client_phone: client.phone || '',
      engineer_id: client.last_engineer_id || prev.engineer_id,
      engineer_name: client.last_engineer_name || prev.engineer_name,
      price: client.last_price || prev.price
    }));
    setShowSuggestions(false);
  };

  // Submit quick book
  const handleQuickBook = async () => {
    if (!quickBook.client_name || !quickBook.time) {
      toast.error('Name and time are required');
      return;
    }
    try {
      setSubmittingQuickBook(true);
      const res = await axios.post(`${API}/admin/quick-book`, {
        ...quickBook,
        date: quickBookDate
      });
      toast.success(res.data.message);
      setShowQuickBook(false);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book');
    } finally {
      setSubmittingQuickBook(false);
    }
  };

  // Handle reschedule
  const handleReschedule = async () => {
    if (!rescheduleData.date || !rescheduleData.time) {
      toast.error('Please select both date and time');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API}/bookings/${selectedBooking.id}/reschedule`,
        {
          new_date: rescheduleData.date,
          new_time: rescheduleData.time,
          notify_client: false,
          is_admin: true
        }
      );
      
      toast.success(response.data.message);
      setShowBookingModal(false);
      setIsRescheduling(false);
      fetchBookings();
    } catch (error) {
      console.error('Error rescheduling:', error);
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setProcessing(false);
    }
  };

  // Week range display
  const weekRangeDisplay = useMemo(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 6);
    return `${currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [currentWeekStart]);

  // Time slots for reschedule (using module-level TIME_SLOTS)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-purple-900/10 border border-purple-500/20 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-400" />
          <h2 className="text-lg font-bold text-white">Weekly Schedule</h2>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Engineer Filter */}
          <select
            value={selectedEngineer}
            onChange={(e) => setSelectedEngineer(e.target.value)}
            className="flex-1 sm:flex-none bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="all">All Engineers</option>
            {engineers.map(eng => (
              <option key={eng.id} value={eng.id}>{eng.name}</option>
            ))}
          </select>
          
          {/* Week Navigation */}
          <div className="flex items-center gap-1 bg-black/40 border border-purple-500/30 rounded-lg p-1">
            <button
              onClick={goToPreviousWeek}
              className="p-1.5 hover:bg-purple-600/30 rounded transition-colors text-gray-400 hover:text-white"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded font-semibold transition-colors"
            >
              Today
            </button>
            <button
              onClick={goToNextWeek}
              className="p-1.5 hover:bg-purple-600/30 rounded transition-colors text-gray-400 hover:text-white"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Week Range */}
      <div className="text-center text-base font-semibold text-gray-300">
        {weekRangeDisplay}
      </div>

      {/* Sessions List by Day */}
      <div className="space-y-3">
        {weekDates.map((date) => {
          const dateStr = date.toISOString().split('T')[0];
          const dayBookings = bookingsByDate[dateStr] || [];
          const dayIsToday = isToday(date);
          
          return (
            <div key={dateStr} className={`rounded-xl border ${dayIsToday ? 'border-purple-500/50 bg-purple-900/20' : 'border-purple-500/20 bg-purple-900/10'}`}>
              {/* Day Header */}
              <div className={`px-4 py-2 border-b ${dayIsToday ? 'border-purple-500/30 bg-purple-600/20' : 'border-purple-500/10'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${dayIsToday ? 'text-purple-400' : 'text-gray-400'}`}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span className={`text-base font-bold ${dayIsToday ? 'text-white' : 'text-gray-300'}`}>
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    {dayIsToday && (
                      <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded-full font-semibold">
                        Today
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {dayBookings.length} session{dayBookings.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); openQuickBook(dateStr); }}
                    className="ml-2 p-1 bg-green-600 hover:bg-green-500 rounded-md transition-colors"
                    data-testid={`quick-book-btn-${dateStr}`}
                    title="Quick Book"
                  >
                    <Plus size={14} className="text-white" />
                  </button>
                </div>
              </div>
              
              {/* Day Sessions */}
              <div className="p-2">
                {dayBookings.length === 0 ? (
                  <div className="text-center py-3 text-gray-500 text-sm">
                    No sessions scheduled
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayBookings.map((booking) => (
                      <button
                        key={booking.id}
                        onClick={() => handleBookingClick(booking)}
                        className={`w-full text-left p-3 rounded-lg border-l-4 hover:opacity-90 transition-all ${getEngineerColor(booking.engineer_id)}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold truncate">{booking.name}</span>
                              <span className="text-xs text-gray-400 bg-black/30 px-2 py-0.5 rounded">
                                {booking.time}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              <Video size={12} />
                              <span>{booking.engineer_name}</span>
                              <span className="text-gray-600">•</span>
                              <span className="truncate">{booking.service_name}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-400 font-bold text-sm">${booking.price}</span>
                            {booking.status === 'pending_approval' && (
                              <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Engineer Legend */}
      <div className="flex flex-wrap justify-center gap-3 py-3 border-t border-purple-500/20">
        {engineers.map(eng => {
          const colorClass = {
            1: 'bg-purple-500',
            2: 'bg-blue-500', 
            3: 'bg-green-500',
            4: 'bg-orange-500'
          }[eng.id] || 'bg-gray-500';
          
          return (
            <div key={eng.id} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${colorClass}`}></div>
              <span className="text-xs text-gray-400">{eng.name}</span>
            </div>
          );
        })}
      </div>

      {/* Booking Details Modal */}
      {showBookingModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border border-purple-500/30 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-purple-500/20 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">
                {isRescheduling ? 'Reschedule Booking' : 'Booking Details'}
              </h3>
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setIsRescheduling(false);
                }}
                className="p-1.5 hover:bg-purple-600/30 rounded-lg transition-colors text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {!isRescheduling ? (
                <>
                  {/* Booking Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-purple-400" />
                      <span className="text-white font-semibold">{selectedBooking.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-purple-400" />
                      <span className="text-gray-300">{selectedBooking.date} @ {selectedBooking.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Video size={16} className="text-purple-400" />
                      <span className="text-gray-300">{selectedBooking.engineer_name}</span>
                    </div>
                    <div className="text-sm text-gray-400 bg-black/30 rounded-lg p-2">
                      {selectedBooking.service_name}
                    </div>
                    <div className="text-2xl font-bold text-green-400">
                      ${selectedBooking.price}
                    </div>
                    {selectedBooking.email && (
                      <a href={`mailto:${selectedBooking.email}`} className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                        <Mail size={14} />
                        {selectedBooking.email}
                      </a>
                    )}
                    {selectedBooking.phone && (
                      <a href={`tel:${selectedBooking.phone}`} className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300">
                        <Phone size={14} />
                        {selectedBooking.phone}
                      </a>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-purple-500/20">
                    <button
                      onClick={() => setIsRescheduling(true)}
                      className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg transition-colors font-semibold"
                    >
                      <RefreshCw size={16} />
                      Reschedule
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Reschedule Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1.5">New Date</label>
                      <input
                        type="date"
                        value={rescheduleData.date}
                        onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                        className="w-full bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2.5 text-white focus:border-purple-500 focus:outline-none"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1.5">New Time</label>
                      <select
                        value={rescheduleData.time}
                        onChange={(e) => setRescheduleData({...rescheduleData, time: e.target.value})}
                        className="w-full bg-black/40 border border-purple-500/30 rounded-lg px-3 py-2.5 text-white focus:border-purple-500 focus:outline-none"
                      >
                        <option value="">Select time</option>
                        {TIME_SLOTS.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Reschedule Actions */}
                  <div className="flex gap-2 pt-4 border-t border-purple-500/20">
                    <button
                      onClick={() => setIsRescheduling(false)}
                      className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2.5 rounded-lg transition-colors font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReschedule}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg transition-colors font-semibold disabled:opacity-50"
                    >
                      {processing ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                      Confirm
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Book Modal */}
      {showQuickBook && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQuickBook(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border border-green-500/30 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="quick-book-modal">
            <div className="p-4 border-b border-green-500/20 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-white">Quick Book</h3>
                <p className="text-xs text-gray-400">{new Date(quickBookDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
              </div>
              <button onClick={() => setShowQuickBook(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Client Name with Auto-Suggest */}
              <div className="relative">
                <label className="block text-sm text-gray-400 mb-1">Client Name *</label>
                <input
                  type="text"
                  value={quickBook.client_name}
                  onChange={e => handleNameChange(e.target.value)}
                  onFocus={() => { if (!quickBook.client_name) fetchSuggestions(''); }}
                  placeholder="Start typing client name..."
                  className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2.5 text-white focus:border-green-500 outline-none"
                  data-testid="quick-book-name"
                  autoFocus
                  autoComplete="off"
                />
                {showSuggestions && clientSuggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden shadow-xl max-h-48 overflow-y-auto">
                    {clientSuggestions.map((c, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-800 flex items-center justify-between border-b border-gray-800 last:border-0"
                        data-testid={`client-suggestion-${idx}`}
                      >
                        <div>
                          <span className="text-white text-sm font-medium">{c.name}</span>
                          <span className="text-gray-500 text-xs ml-2">{c.email}</span>
                        </div>
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                          {c.sessions}x
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Engineer */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Engineer</label>
                <div className="grid grid-cols-2 gap-2">
                  {engineers.filter(e => e.id !== 3 && e.bookable !== false).map(eng => (
                    <button
                      key={eng.id}
                      onClick={() => setQuickBook({...quickBook, engineer_id: eng.id, engineer_name: eng.name})}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        quickBook.engineer_id === eng.id
                          ? 'bg-purple-600 text-white border border-purple-400'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`quick-book-eng-${eng.id}`}
                    >
                      {eng.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time - Tap Grid */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Time *</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {TIME_SLOTS.map(t => (
                    <button
                      key={t}
                      onClick={() => setQuickBook({...quickBook, time: t})}
                      className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${
                        quickBook.time === t
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                      data-testid={`quick-book-time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hours + Price Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Hours</label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 6].map(h => (
                      <button
                        key={h}
                        onClick={() => setQuickBook({...quickBook, hours: h})}
                        className={`flex-1 py-2 rounded text-sm font-bold transition-all ${
                          quickBook.hours === h
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                        data-testid={`quick-book-hours-${h}`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Price ($)</label>
                  <div className="relative">
                    <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="number"
                      value={quickBook.price}
                      onChange={e => setQuickBook({...quickBook, price: parseFloat(e.target.value) || 0})}
                      className="w-full pl-8 pr-3 py-2 bg-black/40 border border-gray-700 rounded-lg text-white focus:border-green-500 outline-none"
                      data-testid="quick-book-price"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map(pm => (
                    <button
                      key={pm.id}
                      onClick={() => setQuickBook({...quickBook, payment_method: pm.id})}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        quickBook.payment_method === pm.id
                          ? `${pm.color} text-white`
                          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`quick-book-pay-${pm.id}`}
                    >
                      {pm.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button
                onClick={handleQuickBook}
                disabled={submittingQuickBook || !quickBook.client_name || !quickBook.time}
                className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 text-white font-bold rounded-lg transition-all"
                data-testid="quick-book-submit"
              >
                {submittingQuickBook ? 'Booking...' : `Book & Block — ${quickBook.client_name || 'Client'} at ${quickBook.time || '...'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyScheduleCalendar;
